<template>
<div class="chapter">
<div class="inner_page">
      ﻿

	
		<title>Collection of Sand: Essays</title>
<meta content="text/html; charset=utf-8" http-equiv="Content-Type"/>
<link href="../styles/stylesheet.css" rel="stylesheet" type="text/css"/>
<h2 class="EB04MainHead" id="page_10">How New the New World Was</h2>
<p class="EB02BodyTextFullOut" id="col0000108">Discovering the New World was a very
			difficult enterprise, as we have all been taught. But even more difficult, once the New
			World was discovered, was <em>seeing</em> it, understanding that it was <em>new</em>,
			entirely <em>new</em>, different from anything one had expected to find as <em>new</em>.
			And the question that spontaneously arises is: if a New World were discovered now, would
			we be able to <em>see it</em>? Would we know how to rid our minds of all the images we
			have become accustomed to associate with the expectation of a world different from our
			own (images from science fiction, for instance) in order to grasp the real difference
			that would be presented to our gaze?</p>
<p class="EB03BodyTextIndented" id="col0000109">We can instantly reply that something has
			changed since the time of Columbus: in the last few centuries man has developed a
			capacity for objective observation, a scrupulousness about precision in establishing
			analogies and differences, a curiosity for everything that is unusual and unexpected,
			and these are all qualities that our predecessors in the ancient world and in the Middle
			Ages apparently did not possess. It is precisely from the discovery of America, we can
			say, that the relationship with what is new changes in human consciousness. And it is
			for that very reason that we usually say that the modern era began then.</p>
<p class="EB03BodyTextIndented" id="col0000110">But will it really be like this? Just as the
			first explorers of America did not know at what point they would either be proved wrong
			or have their familiar preconceptions confirmed, so we too could walk past things never
			seen before without realizing it, for our eyes and minds are used to selecting and
			cataloguing only that which responds to tried and tested classifications. Perhaps a New
			World opens up every day and we don’t see it.</p>
<p class="EB03BodyTextIndented" id="page_11">These thoughts came to mind while visiting the
			exhibition America Seen by Europe, an exhibition that brings together more than 350
			paintings, prints and objects at the Grand Palais in Paris. All of them represent
			European images of the New World, from the earliest reports that came back after the
			voyage of Columbus’s caravels to the gradual understanding that emerged from
			accounts of the exploration of the continent.</p>
<p class="EB03BodyTextIndented" id="col0000111">These are the shores of Spain: it was from
			here that King Ferdinand of Castille gave orders for the caravels to set sail. And this
			stretch of sea is the Atlantic Ocean which Christopher Columbus crossed to reach the
			fabled islands of the Indies. Columbus leans out from the prow and what does he see? A
			procession of naked men and women coming out of their huts. Barely a year had passed
			since Columbus’s first voyage, and this was how a Florentine engraver represented
			the discovery of what at that time people did not know would become America. Nobody yet
			suspected that a new era in the history of the world had opened up, but the excitement
			aroused by this event had spread throughout Europe. On his return, Columbus’s
			report instantly inspired an epic in octaves in the style of a chivalric poem by the
			Florentine Giuliano Dati, and this engraving is in fact an illustration from that
			book.</p>
<p class="EB03BodyTextIndented" id="col0000112">The characteristic of the inhabitants of the
			new lands that most struck Columbus and all the early explorers was their nakedness, and
			this was the first detail that worked on the illustrators’ imaginations. Men are
			portrayed as still having beards: the news that the Indians had smooth cheeks apparently
			had not yet spread. With Columbus’s second voyage and especially with the more
			detailed and colourful reports by Amerigo Vespucci, another feature as well as their
			nakedness fired the European imagination: cannibalism.</p>
<p class="EB03BodyTextIndented" id="col0000113">Seeing a group of Indian women on the shore—Vespucci tells us—the Portuguese sent ashore one of their sailors, who was famous
			for his handsomeness, to talk with the Indian women. They surrounded him, lavishing
			embraces and expressions of admiration on him, but meanwhile one of their number hid
			behind his back and clubbed him on the head, stunning him. The unfortunate man was
			dragged away, cut into pieces, roasted and devoured.</p>
<p class="EB03BodyTextIndented" id="page_12">The first question Europeans asked about the
			inhabitants of the New World was: are they really human? Classical and medieval
			traditions spoke of remote areas inhabited by monsters. But the lie was soon given to
			such legends: Indians were not only human beings, but specimens of classical beauty.
			That was how the myth arose of their happy life, unburdened by property or labour, as in
			the Golden Age or Earthly Paradise.</p>
<p class="EB03BodyTextIndented" id="col0000114">After the crude engravings on wood we find
			the depiction of Indians in paintings. The first American we see portrayed in the
			history of European painting is one of the Three Magi, in a Portuguese painting of 1505,
			in other words barely a dozen years after Columbus’s first voyage, and even less
			time after the Portuguese landing in Brazil. It was still believed that the new lands
			were part of the Far East of Asia. It was traditional for the Three Magi in paintings of
			the Nativity to be represented in oriental garments and head-gear. But now that the
			explorers’ reports provided direct evidence of how these legendary inhabitants of
			‘India’ looked, painters brought themselves up to date. The Indian Magus was
			portrayed as wearing a feather headdress, as certain Brazilian tribes do, and carrying
			in one of his hands a Tupinambá arrow. Since this was a painting for a church, this
			character could not be portrayed naked: he has been given a Western waistcoat and
			trousers.</p>
<p class="EB03BodyTextIndented" id="col0000115">In 1537 Pope Paul III declared: ‘The
			Indians are truly human . . . not only are they able to understand the Catholic
			faith, but they are extremely keen to receive it.’</p>
<p class="EB03BodyTextIndented" id="col0000116">Feather headdresses, weapons, fruits and
			animals from the New World started arriving in Europe. In 1517 a German engraver drawing
			a procession of inhabitants from Calcutta, mixes Asiatic elements (such as an elephant
			and its mahout, bulls draped in garlands, rams with huge tails) with details that come
			from the recent discoveries: a feather headdress (and actually clothes made of feathers
			that are totally imaginary), an Ara parrot from Brazil and also two corn cobs—maize
			was the cereal that was destined to play such a major role in the agriculture and diet
			of Northern Italy, but its American origin would be soon forgotten since the Italian
			word is ‘granturco’ [literally Turkish grain].</p>
<p class="EB03BodyTextIndented" id="page_13">It is thanks to the work of the great
			cartographers of the sixteenth century that we see not only the new territories taking
			shape, but also the fauna, flora and customs of these peoples giving us their first true
			images. Working at close quarters with the explorers, the map-makers had access to
			information at first hand. The outlines of the Atlantic coasts were largely known when
			the new lands were still thought to be an appendix of Asia. Thus in a silver globe of
			1530 the Gulf of Mexico is called ‘The Sea of Cathay’, and South America is
			‘Cannibal Land’.</p>
<p class="EB03BodyTextIndented" id="col0000117">It is in a German map that the name
				<em>America</em> appears for the first time, meaning ‘Amerigo’s
			Land’, because it was mainly through the reports of Vespucci’s voyages that
			Europe had taken on board the geographical significance of the discoveries. It was only
			after the arrival of the Florentine merchant’s letters that Europe realized that
			what was opening up for the old continent was indeed a New World, of enormous dimensions
			and with its own characteristics.</p>
<p class="EB03BodyTextIndented" id="col0000118">Now suddenly in maps of the time America is
			detached from Asia. All that is known of North America (here called ‘Land of
			Cuba’) is a small strip of coastline, and it is thought to be near Japan (called
			‘Zipangri’). The name ‘America’ is applied only to Southern
			America, also called ‘Terranova’ and inhabited, of course, by cannibals. The
			continent has by now acquired an autonomous outline, but it is still seen—even in its
			shape—mainly as an obstacle, a barrier separating us from China and India.</p>
<p class="EB03BodyTextIndented" id="col0000119">In maps drawn up by Mercator, the inventor
			of a new method of cartographic projection, the name ‘America’ is now
			applied to the northern part of the continent as well, and the word is placed alongside
			Labrador, which was then called the Land of Cod.</p>
<p class="EB03BodyTextIndented" id="col0000120">The ideas people had of the Indians were
			polarized for a long time between two opposite myths: the myth of the natural happiness
			of an innocent life, as in the Garden of Eden, and that of ruthless ferocity: stories of
			flaying and torturing. But there were signs of a growing outrage at the cruelty of the
			Spanish, the slaughter and pillage carried out by the Conquistadors.</p>
<p class="EB03BodyTextIndented" id="col0000121">It is only towards the close of the
			sixteenth century that we really start to see Indians face to face. And this too was
			thanks to a <a id="page_14">cartographer and draughtsman, the Englishman John White,
			who in 1585 followed the expedition led by Sir Walter Raleigh, founder of the first
			English colony beyond the Atlantic, Virginia. White’s seventy-six watercolours,
			now in the British Museum, constitute the first evidence of America drawn from life by a
			painter. White did not just draw the costumes and activities of the Red Indians, but
			also the animals of North America: the flamingos, iguanas, land-crabs, turtles, flying
			fish and the huge variety of aquatic fauna.</a></p><a id="page_14">
<p class="EB03BodyTextIndented" id="col0000122">That America had a fauna and flora
			completely different from those of the Old World was a fact that took a long time to be
			recognized by Europeans. Right from his first voyage Columbus had brought back to Spain
			some parrots, Ara parrots, which were much bigger than African parrots. These aroused
			instant curiosity and were inserted by Raphael in the grotesque-style decorations in the
			Vatican Loggia.</p>
<p class="EB03BodyTextIndented" id="col0000123">But on the whole the new animals from
			America do not seem to have aroused much excitement. People soon began to rear turkeys
			in Europe, but they believed wrongly that they were of Asian origin, confusing them with
			guinea-fowl.</p>
<p class="EB03BodyTextIndented" id="col0000124">The animal that most caught the imagination
			was the armadillo, so much so that in allegorical representations America was portrayed
			as a naked woman, armed with a bow and arrows, riding on an armadillo.</p>
<p class="EB03BodyTextIndented" id="col0000125">The truth is that in this immense and
			fertile continent Europeans perhaps expected to find fauna of mastodontic proportions
			and were rather disappointed. America has plenty of strange animals but most of them are
			of modest size. That is why the makers of the Gobelin tapestries felt the need to add to
			their luxuriant vision of the flora and fauna of Brazil animals that have nothing to do
			with America. They contain the most typical zoological representatives of the New World,
			such as the anteater, the tapir, the toucan and the boa constrictor, but also an African
			elephant, an Asiatic peacock and a horse of the kind that the Europeans imported into
			America.</p>
</a><p class="EB03BodyTextIndented" id="col0000126"><a id="page_14">Just as slow, but with much more important
			consequences, was the conquest of Europe by American plants. The potato, the tomato,
			corn and cocoa, which were to have a key role in the agriculture and </a><a id="page_15">diet of the whole of the West, as well as cotton and rubber, which would dominate so
			much of our industrial production, and tobacco, which was to play such an important part
			in behavioural habits, all took a long time to be recognized as new plants. In the
			sixteenth century the study of nature was still based on Greek and Latin authors: it was
			not the new and the unusual that attracted scholars but only that which, rightly or
			wrongly, could be classified using the names handed down by classical texts.</a></p><a id="page_15">
<p class="EB03BodyTextIndented" id="col0000127">In the exhibition we see a Flemish or German
			watercolour dated 1588 which has extraordinary historical value since it is the first
			known representation of a potato (which had been imported from Peru to Spain a few years
			previously), and a print which was the first illustration of a tobacco plant ever to be
			published, in 1574, in Antwerp. The small head of an Indian exhaling clouds of smoke
			through a strange, vertical pipe records that curious custom which no explorer had ever
			failed to note, and to which were attributed sometimes therapeutic and sometimes toxic
			properties.</p>
<p class="EB03BodyTextIndented" id="col0000128">In the seventeenth century it was the Dutch
			who, after hounding the Spanish out of Brazil and before being chased out in their turn
			by the Portuguese, sent scientists and artists to study nature in the colony. Albert
			Eckhout signals the meeting between Dutch nature and Brazilian vegetation. Water-melons,
			cashews, a custard-apple, a passion flower and a pineapple stand out against the sky
			like a mountain of tastes and perfumes. American pumpkins and cucumbers mingle with
			European cabbages and turnips in celebration of the unification of the world of
			vegetables on both sides of the Atlantic.</p>
<p class="EB03BodyTextIndented" id="col0000129">A painting by Franz Jansz Post, today in the
			Louvre, marks the moment when Dutch landscape painting comes into contact with Brazilian
			nature. And here it is really an <em>other</em> world that opens up before our eyes,
			giving us a sense of vertigo: a military fort that is almost lost amid the broad, calm
			expanse of a river; in the foreground stands a cactus that has as many branches as a
			tree, a strange animal (it’s a capybara, the largest extant rodent); and all
			around is heat that intensifies the heaviness of the air.</p>
</a><p class="EB03BodyTextIndented" id="col0000130"><a id="page_15">Through the seventeenth-century paintings of
			Franz Post in Brazil we can still experience the sense of anxiety in discovery, the
			upheaval </a><a id="page_16">caused by the encounter with something undefined, something
			that does not fit neatly within our expectations. The first thought suggested by the
			exhibition in the Grand Palais is that the Old World catches the imagery of the New most
			forcefully when it still does not know precisely what it is dealing with, when
			information is scarce and incomplete, and it is difficult to separate reality from
			mistakes and fantasy.</a></p><a id="page_16">
<p class="EB03BodyTextIndented" id="col0000131">In that same seventeenth century when Dutch
			painters discovered Brazil, America became an allegorical personage in the works of
			other artists: it was classified as one of the four parts of the world, and it was
			accorded a series of attributes like any other mythological figure.</p>
<p class="EB03BodyTextIndented" id="col0000132">The internal differences within America are
			recorded in turn in a summary categorization of the various colonies. In order to teach
			the young Louis XIV geography, he was made to play with geographical-allegorical maps
			drawn by Stefano Della Bella.</p>
<p class="EB03BodyTextIndented" id="col0000133">For other painters America offered, almost
			without mystery any more, a series of stunning views that would enhance the European
			tradition of landscape painting.</p>
<p class="EB03BodyTextIndented" id="col0000134">From the eighteenth century America becomes
			for Europe the embodiment of political and intellectual ideas and myths:
			Rousseau’s noble savage, Montesquieu’s democracy, the Romantic fascination
			with Red Indians, the struggle against slavery.</p>
<p class="EB03BodyTextIndented" id="col0000135">This allegory corresponds to Europe’s
			need to think of America through its own structures, to make conceptually definable the
			thing that was and remains the <em>difference</em>, perhaps one might say the hard core
			of America, in other words the fact that it always has something to say to Europe—from
			Columbus’s first arrival there to today—something that Europe does not know.</p>
</a><p class="EB03BodyTextIndented" id="col0000136"><a id="page_16">The allegorical constant is stressed by the
			final piece in the exhibition, a French painting from the end of the nineteenth century
			reminding us that the Statue of Liberty was designed and built in Paris between 1871 and
			1886. In order to complete the project the restorer of Notre Dame, Viollet-Le-Duc, and
			the engineer Eiffel, the tower’s architect, worked alongside the sculptor
			Bartholdi. Just as today it stands against the backdrop of skyscrapers, so then it </a><a id="page_17">towered above Paris’s mansard roofs, before being dismantled and
			transported to New York by ship.</a></p><a id="page_17">
<p class="EB03BodyTextIndented" id="col0000137">At this point the exhibition ends, and maybe
			it could not have gone any further, because in the last hundred years the terms of
			comparison have changed. There is no longer a Europe that can look down on America from
			the height of its past, its knowledge and its sensibilities: Europe now contains within
			itself so much of America—just as America carries within itself so much of Europe—that the interest in looking at each other, which is just as strong and never
			disappoints—resembles more and more what one feels when looking into a mirror: a
			mirror that is able to reveal something of the past or the future to us.</p>
<p class="EB18EpigraphSource" id="col0000138">[1976]</p>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter002.vue?3cdab396